import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GraduationCap, Search, Download } from "lucide-react";
import { api, roleLabel } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const LEVEL_META = {
  not_assessed: { label: "Not assessed", cls: "text-slate-400" },
  developing: { label: "Developing", cls: "text-amber-700" },
  competent: { label: "Competent", cls: "text-sky-700" },
  expert: { label: "Expert", cls: "text-emerald-700" },
};
const LEVEL_DOT = {
  not_assessed: "bg-slate-200", developing: "bg-amber-400", competent: "bg-sky-500", expert: "bg-emerald-500",
};
const CERT_META = {
  missing: { cls: "bg-slate-100 text-slate-400 border-slate-200", label: "—" },
  valid: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Valid" },
  expiring: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Expiring" },
  expired: { cls: "bg-red-50 text-red-700 border-red-200", label: "Expired" },
};

function certStatus(expiry) {
  if (!expiry) return "missing";
  const d = new Date(expiry);
  if (Number.isNaN(d.getTime())) return "missing";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0) return "expired";
  if (diff <= 60) return "expiring";
  return "valid";
}
function eleapCls(p) {
  if (p >= 90) return "text-emerald-700";
  if (p >= 70) return "text-amber-700";
  return "text-red-600";
}

export default function TrainingMatrix() {
  const [data, setData] = useState({ skills: [], cert_types: [], skill_levels: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState(searchParams.get("region") || "all");
  const [role, setRole] = useState("all");

  useEffect(() => {
    api.get("/training-matrix").then((r) => setData(r.data)).finally(() => setLoading(false));
    api.get("/regions").then((r) => setRegions(r.data));
    api.get("/job-roles").then((r) => setJobRoles(r.data));
  }, []);

  const rows = useMemo(() => {
    return data.rows.filter((r) => {
      if (search && !r.full_name.toLowerCase().includes(search.toLowerCase())) return false;
      if (region !== "all") {
        if (region === "__none__") { if (r.region_id) return false; }
        else if (r.region_id !== region) return false;
      }
      if (role !== "all" && r.job_role !== role) return false;
      return true;
    });
  }, [data.rows, search, region, role]);

  const setSkill = async (empId, skillId, level) => {
    setData((d) => ({ ...d, rows: d.rows.map((r) => (r.id === empId ? { ...r, skills: { ...r.skills, [skillId]: level } } : r)) }));
    try { await api.put(`/employees/${empId}/skills`, { skill_id: skillId, level }); }
    catch (e) { toast.error("Could not update skill"); }
  };
  const setCert = async (empId, certId, expiry) => {
    const status = certStatus(expiry);
    setData((d) => ({ ...d, rows: d.rows.map((r) => (r.id === empId ? { ...r, certs: { ...r.certs, [certId]: { expiry_date: expiry || null, status } } } : r)) }));
    try { await api.put(`/employees/${empId}/certificates`, { cert_type_id: certId, expiry_date: expiry || null }); }
    catch (e) { toast.error("Could not update certificate"); }
  };

  const ragByRegion = useMemo(() => {
    const groups = {};
    data.rows.forEach((r) => {
      const key = r.region_name || "Unassigned";
      const g = groups[key] || (groups[key] = { region: key, regionId: r.region_id || "__none__", green: 0, amber: 0, red: 0, total: 0 });
      let expired = false, expiring = false;
      Object.values(r.certs || {}).forEach((c) => { if (c.status === "expired") expired = true; else if (c.status === "expiring") expiring = true; });
      const s = expired || r.elearning_percentage < 70 ? "red" : (expiring || r.elearning_percentage < 90 ? "amber" : "green");
      g[s] += 1; g.total += 1;
    });
    return Object.values(groups).sort((a, b) => a.region.localeCompare(b.region));
  }, [data.rows]);

  const exportCsv = () => {
    const cols = ["Name", "Job role", "Region", "eLeaP %", ...data.skills.map((s) => s.name), ...data.cert_types.map((c) => c.name)];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.map(esc).join(",")];
    rows.forEach((r) => {
      const cells = [r.full_name, r.job_role, r.region_name, r.elearning_percentage];
      data.skills.forEach((s) => cells.push((LEVEL_META[r.skills[s.id] || "not_assessed"] || {}).label || ""));
      data.cert_types.forEach((c) => { const cert = r.certs[c.id]; cells.push(cert && cert.expiry_date ? `${cert.expiry_date} (${cert.status})` : "missing"); });
      lines.push(cells.map(esc).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "training-matrix.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Matrix exported to CSV");
  };

  return (
    <div className="space-y-6" data-testid="training-matrix-page">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Training Matrix</h1>
        <p className="mt-1 text-sm text-slate-500">
          Company-wide view of skills, eLearning and certificate expiry across every staff member. Everyone can view;
          you can edit the people you manage.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="matrix-search" placeholder="Search staff…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56 pl-9" />
        </div>
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger data-testid="matrix-region-filter" className="w-44"><SelectValue placeholder="Region" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All regions</SelectItem>
            {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="matrix-role-filter" className="w-44"><SelectValue placeholder="Job role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All job roles</SelectItem>
            {jobRoles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-slate-400">{rows.length} staff</span>
          <button onClick={exportCsv} data-testid="matrix-export-csv" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50">
            <Download className="h-4 w-4" /> Download CSV
          </button>
        </div>
      </div>

      {ragByRegion.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="rag-overview">
          {ragByRegion.map((g) => (
            <button
              key={g.region}
              data-testid="rag-region"
              onClick={() => setRegion((cur) => (cur === g.regionId ? "all" : g.regionId))}
              className={`rounded-xl border bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${region === g.regionId ? "border-orange-400 ring-2 ring-orange-200" : "border-slate-200"}`}
            >
              <div className="mb-2 flex items-center justify-between">
                <p className="font-heading text-sm font-bold text-slate-800">{g.region}</p>
                <span className="text-xs text-slate-400">{g.total} staff</span>
              </div>
              <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100">
                {g.green > 0 && <div style={{ width: `${(g.green / g.total) * 100}%` }} className="bg-emerald-500" />}
                {g.amber > 0 && <div style={{ width: `${(g.amber / g.total) * 100}%` }} className="bg-amber-400" />}
                {g.red > 0 && <div style={{ width: `${(g.red / g.total) * 100}%` }} className="bg-red-500" />}
              </div>
              <div className="mt-2 flex gap-3 text-xs">
                <span className="text-emerald-600">{g.green} compliant</span>
                <span className="text-amber-600">{g.amber} attention</span>
                <span className="text-red-600">{g.red} at risk</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
        <Legend items={[["Developing", "bg-amber-400"], ["Competent", "bg-sky-500"], ["Expert", "bg-emerald-500"]]} />
        <span className="text-slate-300">|</span>
        <Legend items={[["Cert valid", "bg-emerald-500"], ["Expiring ≤60d", "bg-amber-400"], ["Expired", "bg-red-500"]]} />
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm esp-scroll">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="sticky left-0 z-20 min-w-[200px] bg-slate-50 px-4 py-3">Staff member</th>
              <th className="min-w-[90px] px-3 py-3 text-center">eLeaP</th>
              {data.skills.map((s) => <th key={s.id} className="min-w-[130px] px-3 py-3">{s.name}</th>)}
              {data.cert_types.map((c) => <th key={c.id} className="min-w-[150px] px-3 py-3">{c.name}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} data-testid="matrix-row" className="hover:bg-slate-50/60">
                <td className="sticky left-0 z-10 min-w-[200px] bg-white px-4 py-2.5 hover:bg-slate-50/60">
                  <p className="font-medium text-slate-800">{r.full_name}</p>
                  <p className="text-xs text-slate-400">{r.job_role}{r.region_name ? ` · ${r.region_name}` : ""}</p>
                </td>
                <td className={`px-3 py-2.5 text-center font-semibold ${eleapCls(r.elearning_percentage)}`}>{r.elearning_percentage}%</td>
                {data.skills.map((s) => {
                  const level = r.skills[s.id] || "not_assessed";
                  return (
                    <td key={s.id} className="px-3 py-2.5">
                      {r.can_manage ? (
                        <select
                          data-testid={`skill-cell-${r.id}-${s.id}`}
                          value={level}
                          onChange={(e) => setSkill(r.id, s.id, e.target.value)}
                          className="w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700"
                        >
                          {data.skill_levels.map((l) => <option key={l} value={l}>{LEVEL_META[l]?.label || l}</option>)}
                        </select>
                      ) : (
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${LEVEL_META[level].cls}`}>
                          <span className={`h-2 w-2 rounded-full ${LEVEL_DOT[level]}`} />{LEVEL_META[level].label}
                        </span>
                      )}
                    </td>
                  );
                })}
                {data.cert_types.map((c) => {
                  const cert = r.certs[c.id] || { expiry_date: null, status: "missing" };
                  const meta = CERT_META[cert.status];
                  return (
                    <td key={c.id} className="px-3 py-2.5">
                      {r.can_manage ? (
                        <div className="flex flex-col gap-1">
                          <input
                            type="date"
                            data-testid={`cert-cell-${r.id}-${c.id}`}
                            value={cert.expiry_date || ""}
                            onChange={(e) => setCert(r.id, c.id, e.target.value)}
                            className={`w-full rounded-md border px-2 py-1 text-xs ${meta.cls}`}
                          />
                        </div>
                      ) : (
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${meta.cls}`}>
                          {cert.expiry_date ? `${meta.label} · ${cert.expiry_date}` : meta.label}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={2 + data.skills.length + data.cert_types.length} className="px-4 py-10 text-center text-slate-400">No staff match your filters.</td></tr>
            )}
            {loading && (
              <tr><td colSpan={2 + data.skills.length + data.cert_types.length} className="px-4 py-10 text-center text-slate-400">Loading matrix…</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {data.skills.length === 0 && data.cert_types.length === 0 && !loading && (
        <p className="flex items-center gap-2 text-sm text-slate-400"><GraduationCap className="h-4 w-4" /> No skills or certificate types defined yet — an admin can add them in Administration.</p>
      )}
    </div>
  );
}

function Legend({ items }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {items.map(([label, dot]) => (
        <span key={label} className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${dot}`} />{label}</span>
      ))}
    </div>
  );
}
